'use strict';

/**
 * VideoPlayer — Shared utility for video poster/play interactions.
 *
 * Handles:
 * - Play button click → hide poster → trigger autoplay
 * - Platform-specific autoplay (YouTube, Vimeo, DailyMotion, MP4)
 * - Internal video pause/end → show poster again (opt-in)
 *
 * Usage:
 *   import VideoPlayer from '../utils/video-player';
 *
 *   VideoPlayer.initPoster( posterEl, containerEl, { resetOnPause: true } );
 */
const VideoPlayer = {

	/**
	 * Start autoplay for the given platform inside a container element.
	 *
	 * @param {HTMLElement} container  - The parent that contains the iframe/video.
	 * @param {string}      platform  - 'youtube' | 'vimeo' | 'dailymotion' | 'mp4' | 'internal'
	 */
	play: function ( container, platform ) {
		switch ( platform ) {
			case 'youtube':
				VideoPlayer._playIframe( container, '.youtube_player', 'autoplay=1&playsinline=1' );
				break;

			case 'vimeo':
				VideoPlayer._playIframe( container, '.vimeo_player', 'autoplay=1&autopause=0' );
				break;

			case 'dailymotion':
				VideoPlayer._playIframe( container, '.dailymotion_player', 'autoplay=1&autopause=0' );
				break;

			case 'mp4':
			case 'internal': {
				const video = container.querySelector('video');
				if ( video ) {
					video.play();
				}
				break;
			}
		}
	},

	/**
	 * Reset a video container back to its initial state (pause, remove autoplay params).
	 *
	 * @param {HTMLElement} container - The parent that contains the iframe/video.
	 */
	reset: function ( container ) {
		const youtubeWrap = container.querySelector('.youtube_player');
		if ( youtubeWrap ) {
			VideoPlayer._resetIframe( youtubeWrap, [
				/[&?]autoplay=1/,
				/[&?]playsinline=1/,
			] );
		}

		const vimeoWrap = container.querySelector('.vimeo_player');
		if ( vimeoWrap ) {
			VideoPlayer._resetIframe( vimeoWrap, [
				/[&?]autoplay=1/,
				/[&?]autopause=0/,
			] );
		}

		const dailymotionWrap = container.querySelector('.dailymotion_player');
		if ( dailymotionWrap ) {
			VideoPlayer._resetIframe( dailymotionWrap, [
				/[&?]autoplay=1/,
				/[&?]autopause=0/,
			] );
		}

		const video = container.querySelector('video');
		if ( video ) {
			video.pause();
			video.currentTime = 0;
		}
	},

	/**
	 * Initialize a poster overlay with play button click handling.
	 *
	 * @param {HTMLElement} poster     - The poster overlay element (has data-type-video attr).
	 * @param {HTMLElement} container  - The parent containing the video/iframe.
	 * @param {Object}      options
	 * @param {string}      options.playBtnSelector  - Selector for play button inside poster.
	 * @param {boolean}     options.resetOnPause     - Re-show poster on internal video pause/end.
	 */
	initPoster: function ( poster, container, options = {} ) {
		const {
			playBtnSelector = 'button',
			resetOnPause = false,
		} = options;

		const playBtn = poster.querySelector( playBtnSelector );

		if ( ! playBtn ) {
			return;
		}

		playBtn.addEventListener( 'click', () => {
			poster.classList.add('hidden');

			const platform = poster.dataset.typeVideo;
			VideoPlayer.play( container, platform );
		} );

		// For internal videos: optionally show poster again on pause/end
		const platform = poster.dataset.typeVideo;
		if ( resetOnPause && ( platform === 'mp4' || platform === 'internal' ) ) {
			VideoPlayer._bindInternalVideoEvents( poster, container );
		}
	},

	// ── Private helpers ──────────────────────────────────────────────────────

	/**
	 * Append autoplay params to an iframe src and re-render it.
	 */
	_playIframe: function ( container, wrapSelector, params ) {
		const wrap   = container.querySelector( wrapSelector );
		const iframe = wrap ? wrap.querySelector('iframe') : container.querySelector('iframe');

		if ( ! iframe ) {
			return;
		}

		let src = iframe.getAttribute('src')
			.replace('autoplay=0', '')
			.replace('muted=0', '')
			.replace('playsinline=0', '');

		src += ( src.includes('?') ? '&' : '?' ) + params;

		const attrs = VideoPlayer._getIframeAttrs( wrapSelector );
		const target = wrap || container;
		target.innerHTML = `<iframe ${attrs} src="${src}"></iframe>`;
	},

	/**
	 * Remove autoplay params from an iframe and re-render.
	 */
	_resetIframe: function ( wrap, patterns ) {
		const iframe = wrap.querySelector('iframe');

		if ( ! iframe ) {
			return;
		}

		let src = iframe.getAttribute('src') || '';
		patterns.forEach( pattern => {
			src = src.replace( pattern, '' );
		} );

		const attrs = VideoPlayer._getIframeResetAttrs( wrap );
		wrap.innerHTML = `<iframe ${attrs} src="${src}"></iframe>`;
	},

	/**
	 * Get standard iframe attributes for a given platform wrapper.
	 */
	_getIframeAttrs: function ( wrapSelector ) {
		switch ( wrapSelector ) {
			case '.youtube_player':
				return 'frameborder="0" allowfullscreen="1" allow="accelerometer; autoplay; playsinline; encrypted-media; gyroscope; picture-in-picture" title="YouTube video player" width="100%" height="100%"';
			case '.vimeo_player':
				return 'width="100%" height="100%" frameborder="0" webkitallowfullscreen mozallowfullscreen allow="autoplay" allowfullscreen';
			case '.dailymotion_player':
				return 'width="100%" height="100%" frameborder="0" webkitallowfullscreen mozallowfullscreen allow="autoplay" allowfullscreen';
			default:
				return 'width="100%" height="100%" frameborder="0" allowfullscreen';
		}
	},

	/**
	 * Get iframe attributes for reset state (non-autoplay).
	 */
	_getIframeResetAttrs: function ( wrap ) {
		if ( wrap.classList.contains('youtube_player') || wrap.closest('.youtube_player') ) {
			return 'frameborder="0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" title="YouTube video player" width="100%" height="100%"';
		}
		if ( wrap.classList.contains('vimeo_player') || wrap.closest('.vimeo_player') ) {
			return 'width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" allowfullscreen';
		}
		return 'width="100%" height="100%" frameborder="0" allowfullscreen';
	},

	/**
	 * Bind pause/end events on internal video to re-show poster.
	 */
	_bindInternalVideoEvents: function ( poster, container ) {
		const video = container.querySelector('video');

		if ( ! video ) {
			return;
		}

		let pauseTimer = null;

		video.addEventListener( 'pause', () => {
			if ( video.ended ) {
				return;
			}
			pauseTimer = setTimeout( () => {
				poster.classList.remove('hidden');
			}, 200 );
		} );

		video.addEventListener( 'seeking', () => {
			clearTimeout( pauseTimer );
		} );

		video.addEventListener( 'play', () => {
			clearTimeout( pauseTimer );
		} );

		video.addEventListener( 'ended', () => {
			clearTimeout( pauseTimer );
			poster.classList.remove('hidden');
			video.currentTime = 0;
		} );
	},
};

export default VideoPlayer;
